import { Injectable } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  ValidatorFn,
  Validators
} from "@angular/forms";
import { InputFormModel, IValidator, ObjectFrom } from "../models/input-form";
import { forkJoin, map, of } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class DynamicFormServices {
  constructor(private formBuilder: FormBuilder) {}

  generateFormGroup(inputForm: ObjectFrom[]) {
    performance.mark("generateFormGroup");
    let formGroup = this.formBuilder.group({});
    inputForm.forEach((riga: InputFormModel[]) => {
      riga.forEach((element: InputFormModel) => {
        let validatorList: ValidatorFn[] = [];
        element.validators &&
          this.validators(element.validators, validatorList);
        formGroup.addControl(
          element.formControlName,
          new FormControl(
            { value: element.value, disabled: element.disabled },
            validatorList
          )
        );
      });
    });
    performance.mark("generateFormGroup-end");
    performance.measure("generateFormGroup", "generateFormGroup", "generateFormGroup");
    return formGroup;
  }

  private validators(valdiators: IValidator, validatorsList: ValidatorFn[]) {
    Object.keys(valdiators).forEach((element) => {
      switch (element) {
        case "required":
          {
            valdiators.required && validatorsList.push(Validators.required);
          }
          break;

        case "email":
          {
            valdiators.email && validatorsList.push(Validators.email);
          }
          break;

        case "pattern":
          {
            validatorsList.push(
              Validators.pattern(valdiators.pattern as string | RegExp)
            );
          }
          break;

        case "maxlength":
          {
            (valdiators.maxlength as number) > 0 &&
              validatorsList.push(
                Validators.maxLength(valdiators.maxlength as number)
              );
          }
          break;

        case "minlength":
          {
            (valdiators.minlength as number) > 0 &&
              validatorsList.push(
                Validators.minLength(valdiators.minlength as number)
              );
          }
          break;

        case "maxVal":
          {
            (valdiators.maxVal as number) > 0 &&
              validatorsList.push(Validators.max(valdiators.maxVal as number));
          }
          break;

        case "minVal":
          {
            (valdiators.minVal as number) > 0 &&
              validatorsList.push(Validators.min(valdiators.minVal as number));
          }
          break;

        default:
          break;
      }
    });
  }

  public setSelectObject(
    inputForm: ObjectFrom[],
    formControlName: string,
    object: any[]
  ) {
    return inputForm.map((row) => {
      performance.mark("setSelectObject-start");
      row.map((element) => {
        if (element.formControlName === formControlName) {
          element.objectSelect = object;
          element.objectSelectBK = object;
        }
      });
      performance.mark("setSelectObject-end");
      performance.measure(
        "setSelectObject",
        "setSelectObject-start",
        "setSelectObject-end"
      );
    });
  }

  public setValue(
    inputForm: ObjectFrom[],
    formControlName: string,
    value: any
  ) {
    return inputForm.map((row) => {
      performance.mark("setValue-start");
      row.map((element) => {
        if (element.formControlName === formControlName) {
          element.value = value;
          element.valueBK = value;
        }
      });
      performance.mark("setValue-end");
      performance.measure("setValue", "setValue-start", "setValue-end");
    });
  }

  public setMinDataAndMaxData(
    inputForm: ObjectFrom[],
    formControlName: string,
    minData?: Date,
    maxDate?: Date
  ) {
    return inputForm.map((row) => {
      row.map((element) => {
        if (element.formControlName === formControlName) {
          if (minData) {
            element.minDate = minData;
          }
          if (maxDate) {
            element.maxDate = maxDate;
          }
        }
      });
    });
  }

  public setMinValAndMaxValValidator(
    inputForm: ObjectFrom[],
    formControlName: string,
    min?: number,
    max?: number
  ) {
    return inputForm.map((row) => {
      row.map((element) => {
        if (element.formControlName === formControlName) {
          if (!element.validators) {
            element.validators = {};
          }
          if (min) {
            element.validators.minVal = min;
          }
          if (max) {
            element.validators.maxVal = max;
          }
        }
      });
    });
  }

  public setMinLengthAndMaxLengthValidator(
    inputForm: ObjectFrom[],
    formControlName: string,
    min?: number,
    max?: number
  ) {
    return inputForm.map((row) => {
      row.map((element) => {
        if (element.formControlName === formControlName) {
          if (!element.validators) {
            element.validators = {};
          }
          if (min) {
            element.validators.minlength = min;
          }
          if (max) {
            element.validators.maxlength = max;
          }
        }
      });
    });
  }

  public setUpdateInputForm(inputForm: ObjectFrom[], object: any) {
    return inputForm.map((row) => {
      performance.mark("setUpdateInputForm-start");
      row.map((element) => {
        element.value = object[element.formControlName];
      });
      performance.mark("setUpdateInputForm-end");
      performance.measure(
        "setUpdateInputForm",
        "setUpdateInputForm-start",
        "setUpdateInputForm-end"
      )
    });
  }

  public returnElementItemForm(inputForm: ObjectFrom[], key: string) {
    let number = performance.now();
    const startMark = `returnElementItemForm-start-${number}`;
    const stopMark = `returnElementItemForm-stop-${number}`;

    performance.mark(startMark);

    let input: InputFormModel | undefined;
    (inputForm ?? []).forEach((row) => {
      if (row.find((item) => item.formControlName === key)) {
        input = row.find((item) => item.formControlName === key);
      }
    });

    performance.mark(stopMark);
    performance.measure("returnElementItemForm", startMark, stopMark);

    return input;
  }

  public checkTheKey(inputForm: ObjectFrom[], key: string) {
    let isFound = false;
    inputForm.forEach((row) => {
      if (row.find((item) => item.formControlName === key)) {
        isFound = true;
      }
    });
    return isFound;
  }

  toMap(inputForm: ObjectFrom[], key: string): Map<string, InputFormModel> {
    performance.mark("toMap-start");
    const mapOfNetworkControl: Map<string, InputFormModel> = new Map();

    for (const row of inputForm ?? []) {
      for (const item of row) {
        if (!mapOfNetworkControl.has(item.formControlName)) {
          mapOfNetworkControl.set(item.formControlName, item);
        }
      }
    }

    performance.mark("toMap-end");
    performance.measure("toMap", "toMap-start", "toMap-end")
    return mapOfNetworkControl;
  }
}
